import { useState, useEffect, useContext, useCallback } from '@wordpress/element';
import { ConfigContext } from '../../index.js';
import { useParams } from 'react-router-dom';
import * as storage from '../../browserStorage';
import LocationGate from './LocationGate';
import AgeGate from './AgeGate';

/**
 * LocationModal Component
 * 
 * This component handles the location selection and age verification process.
 * It manages the display of LocationGate and AgeGate components based on user interactions.
 */
const LocationModal = ({
    retailers,
    retailerId,
    setRetailerId,
    singleRetailer,
    ageGateVisible,
    setAgeGateVisible,
    setLocationRedirect,
    nonHeadlessRetailer,
    nonHeadlessRetailerData,
    setNonHeadlessRetailer,
    setNonHeadlessRetailerData
}) => {
    const params = useParams();
    const { styleOptions, allRetailersConfig, locationGateConfig, nonHeadlessRetailers } = useContext(ConfigContext);

    const [showLocationGate, setShowLocationGate] = useState(true);
    const [showAgeGate, setShowAgeGate] = useState(false);
    const [ageGateType, setAgeGateType] = useState(null);
    const [opacity, setOpacity] = useState(1);
    const [storedRetailerId, setStoredRetailerId] = useState(null);
    const [storedRetailer, setStoredRetailer] = useState(null);
    const [customRetailersOrder, setCustomRetailersOrder] = useState(allRetailersConfig?.customRetailersOrder);
    
    const [newRetailerId, setNewRetailerId] = useState(retailerId);
    
    useEffect(() => {
        if (!newRetailerId && retailerId) {
            setNewRetailerId(retailerId);
        }
    }, [retailerId]);
    
    useEffect(() => {
        if (allRetailersConfig?.locationHomepageRedirect) {
            setLocationRedirect(true);
        }
    }, [allRetailersConfig, setLocationRedirect]);

    // Render HTML helper function
    const renderHtml = html => ({ __html: html });

    // Fade animation functions
    const fadeAnimation = useCallback((startOpacity, endOpacity, callback) => {
        let op = startOpacity;
        const fade = () => {
            if ((startOpacity < endOpacity && op >= endOpacity) || (startOpacity > endOpacity && op <= endOpacity)) {
                clearInterval(interval);
                setOpacity(endOpacity);
                if (callback) callback();
            } else {
                op += (endOpacity - startOpacity) > 0 ? 0.15 : -0.15;
                setOpacity(op);
            }
        };
        const interval = setInterval(fade, 20);
    }, []);

    const fadeOutLocationGate = useCallback((callback) => fadeAnimation(1, 0, callback), [fadeAnimation]);
    const fadeInAgeGate = useCallback(() => fadeAnimation(0, 1), [fadeAnimation]);

    // Handle location selection
    const handleLocationSelect = useCallback((retailer) => {
        setStoredRetailer(retailer);

        if (locationGateConfig?.enableAgeGate) {
            if (retailer.id) {
                setStoredRetailerId(retailer.id);
                setAgeGateType(retailer.ageGateType || locationGateConfig.ageGateType);
            }

            fadeOutLocationGate(() => {
                setShowLocationGate(false);
                setShowAgeGate(true);
                fadeInAgeGate();
            });
        } else {
            handleRetailerSelection(retailer);
        }
    }, [locationGateConfig, fadeOutLocationGate, fadeInAgeGate]);

    // Handle retailer selection
    const handleRetailerSelection = useCallback((retailer) => {
        if (retailer.id) {
            storage.removeItem('NON_HEADLESS_RETAILER');
            console.log('setting retailer id:', retailer.id);
            
            // IMPORTANT: Always set the age gate passed flag first
            // This prevents the gate from re-appearing when retailerId changes
            storage.setItem('HEADLESS_AGE_GATE_PASSED', 'true');
            setAgeGateVisible(false);
            
            // Then set the retailer ID
            setRetailerId(retailer.id);
    
            // First check if we're already on a location page or subpage
            const currentPath = window.location.pathname;
            const locationPath = `/location/${retailer.menuSlug}/`;
            console.log('Current path:', currentPath);
            console.log('Location path:', locationPath);
            
            if (currentPath.startsWith(locationPath)) {
                console.log('Already on location page - just closing age gate');
                return;
            }
    
            // If not on location page, proceed with redirects if enabled
            if (params?.categoryType === 'product' || 
                (params?.viewName === "shop" && params?.locationSlug)) {
                console.log('On product or shop page - not redirecting');
                return;
            }
            else if (params?.viewName === "shop" && retailer?.menuSlug) {
                console.log('On base shop page - not redirecting');
                return;
            }
            else if (allRetailersConfig?.locationMenuRedirect && retailer?.menuSlug) {
                console.log('Menu redirect - redirecting to:', `/shop/${retailer.menuSlug}/`);
                window.location.href = `/shop/${retailer.menuSlug}/`;
            }
            else if (allRetailersConfig?.locationHomepageRedirect && retailer.locationHomepage) {
                console.log('Homepage redirect');
                window.location.href = retailer.locationHomepage;
            }
        } else if (retailer.non_headless_location_homepage) {
            console.log('Non-headless retailer case');
            storage.setItem('NON_HEADLESS_RETAILER', retailer.location_name);
            // Still set age gate passed to avoid re-prompting
            storage.setItem('HEADLESS_AGE_GATE_PASSED', 'true');
            setNonHeadlessRetailerData(retailer);
            window.location.href = retailer.non_headless_location_homepage;
        }
    }, [params, allRetailersConfig, setRetailerId, setAgeGateVisible, setNonHeadlessRetailerData]);

    // Handle age gate completion
    const handleAgeGate = useCallback((retailer) => {
        if (!retailerId && storedRetailerId) {
            setRetailerId(storedRetailerId);
        }

        if (ageGateType === 'birthday') {
            storage.setItem('HEADLESS_BIRTHDAY_GATE_PASSED', 'true');
        }

        storage.setItem('HEADLESS_AGE_GATE_PASSED', 'true');
        
        handleRetailerSelection(retailer);
    }, [retailerId, storedRetailerId, ageGateType, setRetailerId, handleRetailerSelection]);

    // Effects for managing component state
    useEffect(() => {
        const ageGatePassed = storage.getItem('HEADLESS_AGE_GATE_PASSED');

        if ((retailerId || nonHeadlessRetailer) && ageGatePassed !== 'true') {
            setStoredRetailerId(retailerId);
            setShowLocationGate(false);
            setShowAgeGate(true);
        }
    }, [retailerId, nonHeadlessRetailer]);

    useEffect(() => {
        if (storedRetailer) {
            setAgeGateType(storedRetailer.ageGateType || locationGateConfig.ageGateType);
        }
    }, [storedRetailer, locationGateConfig]);

    useEffect(() => {
        if (retailerId) {
            const retailerAgeGateType = retailers[retailerId]?.ageGateType || storedRetailer?.ageGateType || locationGateConfig.ageGateType;
            setAgeGateType(retailerAgeGateType);
        }
    }, [retailerId, retailers, storedRetailer, locationGateConfig]);

    return (
        <div
            className={`headless-location-modal-overlay ${ageGateVisible ? 'hlm-visible' : ''}`}
            style={locationGateConfig?.locationGateBgImage ? { backgroundImage: `url(${locationGateConfig.locationGateBgImage})` } : {}}
        >
            {locationGateConfig?.locationGateBgVideo && (
                <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    poster={locationGateConfig.locationGateBgImage}
                    style={{ position: 'fixed', width: '100%', height: '100%', top: '0', left: '0', objectFit: 'cover' }}
                >
                    <source src={locationGateConfig.locationGateBgVideo} type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
            )}
            <div className='headless-location-modal-content'>
                {showLocationGate && !singleRetailer && (
                    <div style={{ opacity }}>
                        <LocationGate
                            locationGateConfig={locationGateConfig}
                            renderHtml={renderHtml}
                            allRetailersConfig={allRetailersConfig}
                            retailers={retailers}
                            setRetailerId={setRetailerId}
                            nonHeadlessRetailers={nonHeadlessRetailers}
                            styleOptions={styleOptions}
                            handleLocationSelect={handleLocationSelect}
                            customRetailersOrder={customRetailersOrder}
                        />
                    </div>
                )}
                {showAgeGate && (
                    <div style={{ opacity }}>
                        <AgeGate
                            locationGateConfig={locationGateConfig}
                            renderHtml={renderHtml}
                            handleAgeGate={handleAgeGate}
                            storedRetailer={storedRetailer}
                            retailers={retailers}
                            retailerId={newRetailerId}
                            setAgeGateType={setAgeGateType}
                            ageGateType={ageGateType}
                            nonHeadlessRetailer={nonHeadlessRetailer}
                            nonHeadlessRetailerData={nonHeadlessRetailerData}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default LocationModal;
